import {
  Badge,
  Buzzebees,
  CartCountResponse,
  ConfirmOtpResponse,
  ErrorResponse,
  ExpiringPoints,
  ProfileResponse,
  ServiceResponse,
  StatusResponse,
  Trace,
  UpdatedPoints,
} from '@bzbs/react-api-client';
import { create } from 'zustand';
import { eventEmitter } from '../service';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';

export type UpdateProfileParams = {
  profileImage?: File | { uri: string; name: string; type: string };
  firstName?: string;
  lastName?: string;
  contactNumber?: string;
  email?: string;
  notification?: boolean;
  locale?: number;
  title?: string;
  gender?: string;
  birthDate?: number;
  address?: string;
  subdistrictCode?: number;
  subdistrictName?: string;
  districtCode?: number;
  districtName?: string;
  provinceCode?: number;
  provinceName?: string;
  countryCode?: string;
  countryName?: string;
  zipCode?: string;
  idCard?: string;
  passport?: string;
  maritalStatus?: string;
  village?: string;
  building?: string;
  number?: string;
  moo?: string;
  room?: string;
  floor?: string;
  soi?: string;
  city?: string;
  road?: string;
  landmark?: string;
  alternateContactNumber?: string;
  homeContactNumber?: string;
  nationality?: string;
  religion?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  income?: string;
  interests?: string;
  region?: string;
  phonepurchase?: number;
  highestEducation?: string;
  occupation?: string;
  remark?: string;
  displayName?: string;
  options?: { [key: string]: any };
};

export type UserState = {
  user: ProfileResponse | null;
  points: number;
  recentPoints: number;
  expiringPoints: UpdatedPoints[];
  recentBadge: Badge | null;
  recentTrace: Trace | null;
  cartCount: number;
  isLoading: boolean;
  error: ErrorResponse | null;
  badgeList: Badge[];
  traceList: Trace[];
};

export type UserActions = {
  fetchUser: () => Promise<ServiceResponse<ProfileResponse>>;
  fetchPoint: () => Promise<ServiceResponse<UpdatedPoints>>;
  fetchExpiringPoints: () => Promise<ServiceResponse<ExpiringPoints>>;
  fetchCartCount: () => Promise<ServiceResponse<CartCountResponse>>;
  editUser: (data: UpdateProfileParams) => Promise<ServiceResponse<ProfileResponse>>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<ServiceResponse<StatusResponse>>;
  changeContactNumber: (
    contactNumber: string,
    otp: string,
    refCode: string
  ) => Promise<ServiceResponse<ConfirmOtpResponse>>;
  changeAvatar: (avatar: File) => Promise<ServiceResponse<ProfileResponse>>;
  consumeRecentPoints: () => void;
  consumeRecentBadge: () => void;
  consumeRecentTrace: () => void;
  deleteUser: () => Promise<ServiceResponse<any>>;
  setError: (error: ErrorResponse | null) => void;
  clear: () => void;
  setupEventListeners: () => void;
  cleanupEventListeners: () => void;
};

export type UserStore = UserState & UserActions;

export const useUserStore = create<UserStore>((set, get) => {
  return {
    user: null,
    points: 0,
    recentPoints: 0,
    expiringPoints: [],
    recentBadge: null,
    recentTrace: null,
    cartCount: 0,
    isLoading: false,
    error: null,
    badgeList: [],
    traceList: [],

    fetchUser: async () => {
      set({ isLoading: true });
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      if (!bzbsService) {
        set({ isLoading: false, error: { type: 'client-error', message: 'Service not configured' } as ErrorResponse });
        return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<ProfileResponse>;
      }
      const response = await bzbsService.profileApi.profile();
      set({ isLoading: false });
      if (response.type === 'success') {
        set({ user: response.model, error: null });
      } else {
        set({ error: response });
      }
      return response;
    },

    fetchPoint: async () => {
      set({ isLoading: true });
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      if (!bzbsService) {
        set({ isLoading: false, error: { type: 'client-error', message: 'Service not configured' } as ErrorResponse });
        return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<UpdatedPoints>;
      }
      const response = await bzbsService.profileApi.points();
      set({ isLoading: false });
      if (response.type === 'success') {
        set({ points: response.model.points ?? 0, error: null });
      } else {
        set({ error: response });
      }
      return response;
    },

    fetchExpiringPoints: async () => {
      set({ isLoading: true });
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      if (!bzbsService) {
        set({ isLoading: false, error: { type: 'client-error', message: 'Service not configured' } as ErrorResponse });
        return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<ExpiringPoints>;
      }
      const response = await bzbsService.profileApi.expiringPoints();
      set({ isLoading: false });
      if (response.type === 'success') {
        set({ expiringPoints: response.model.expiring_points ?? [], error: null });
      } else {
        set({ error: response });
      }
      return response;
    },

    fetchCartCount: async () => {
      set({ isLoading: true });
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      if (!bzbsService) {
        set({ isLoading: false, error: { type: 'client-error', message: 'Service not configured' } as ErrorResponse });
        return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<CartCountResponse>;
      }
      const response = await bzbsService.cartApi.cartCount();
      set({ isLoading: false });
      if (response.type === 'success') {
        set({ cartCount: response.model.cart_count ?? 0, error: null });
      } else {
        set({ error: response, cartCount: 0 });
      }
      return response;
    },

    editUser: async (data: UpdateProfileParams) => {
      set({ isLoading: true });
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      if (!bzbsService) {
        set({ isLoading: false, error: { type: 'client-error', message: 'Service not configured' } as ErrorResponse });
        return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<ProfileResponse>;
      }
      const response = await bzbsService.profileApi.updateProfile(data);
      set({ isLoading: false });
      if (response.type === 'success') {
        set({ user: response.model, error: null });

        const { tokenFunctions, tokenType } = useBuzzebeesAppStore.getState();
        const token = tokenType === 'jwt' ? response.model.Jwt : response.model.Token;
        if (token && tokenFunctions) {
          tokenFunctions.setToken(token);
        }
      } else {
        set({ error: response });
      }
      return response;
    },

    changePassword: async (oldPassword: string, newPassword: string) => {
      set({ isLoading: true });
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      if (!bzbsService) {
        set({ isLoading: false, error: { type: 'client-error', message: 'Service not configured' } as ErrorResponse });
        return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<StatusResponse>;
      }
      const response = await bzbsService.profileApi.changePassword({ current: oldPassword, change: newPassword });
      set({ isLoading: false });
      if (response.type === 'success') {
        set({ error: null });
      } else {
        set({ error: response });
      }
      return response;
    },

    changeContactNumber: async (contactNumber: string, otp: string, refCode: string) => {
      set({ isLoading: true });
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      if (!bzbsService) {
        set({ isLoading: false, error: { type: 'client-error', message: 'Service not configured' } as ErrorResponse });
        return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<ConfirmOtpResponse>;
      }
      const response = await bzbsService.profileApi.changeContactNumber({ contactNumber, otp, refCode });
      set({ isLoading: false });
      if (response.type === 'success') {
        set({ error: null });
      } else {
        set({ error: response });
      }
      return response;
    },

    changeAvatar: async (avatar: File) => {
      set({ isLoading: true });
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      if (!bzbsService) {
        set({ isLoading: false, error: { type: 'client-error', message: 'Service not configured' } as ErrorResponse });
        return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<ProfileResponse>;
      }
      const response = await bzbsService.profileApi.updateProfile({ profileImage: avatar });
      set({ isLoading: false });
      if (response.type === 'success') {
        set({ user: response.model, error: null });
      } else {
        set({ error: response });
      }
      return response;
    },

    consumeRecentPoints: () => {
      set({ recentPoints: 0 });
    },

    consumeRecentBadge: () => {
      const { badgeList } = get();
      set({ recentBadge: null });
      if (badgeList.length > 0) {
        const next = badgeList[0];
        const remainingBadges = badgeList.slice(1);
        set({ recentBadge: next, badgeList: remainingBadges });
      }
    },

    consumeRecentTrace: () => {
      const { traceList } = get();
      set({ recentTrace: null });
      if (traceList.length > 0) {
        const next = traceList[0];
        const remainingTraces = traceList.slice(1);
        set({ recentTrace: next, traceList: remainingTraces });
      }
    },

    deleteUser: async () => {
      set({ isLoading: true });
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      if (!bzbsService) {
        set({ isLoading: false, error: { type: 'client-error', message: 'Service not configured' } as ErrorResponse });
        return { type: 'client-error', message: 'Service not configured' } as ServiceResponse<any>;
      }
      const response = await bzbsService.profileApi.deactivate();
      set({ isLoading: false });
      if (response.type === 'success') {
        get().clear();
      } else {
        set({ error: response });
      }
      return response;
    },

    setError: (error: ErrorResponse | null) => {
      set({ error });
    },

    clear: () => {
      set({
        user: null,
        points: 0,
        recentPoints: 0,
        expiringPoints: [],
        recentBadge: null,
        recentTrace: null,
        cartCount: 0,
        isLoading: false,
        error: null,
        badgeList: [],
        traceList: [],
      });
    },

    setupEventListeners: () => {
      eventEmitter.on('get_points', (buzzebees: Buzzebees) => {
        console.log('get_points', buzzebees);
        const points = buzzebees.points;
        if (points) {
          set({ recentPoints: points });
        }

        const traces = buzzebees.traces;
        if (traces) {
          if (traces.length > 0) {
            const first = traces[0];
            const remainingTraces = traces.slice(1);
            set({ recentTrace: first, traceList: remainingTraces });
          }
        }
      });

      eventEmitter.on('get_badges', (badges: Badge[]) => {
        if (badges.length > 0) {
          const first = badges[0];
          const remainingBadges = badges.slice(1);
          set({ recentBadge: first, badgeList: remainingBadges });
        }
      });
    },

    cleanupEventListeners: () => {
      console.log('UserStore cleanup');
      eventEmitter.clearListeners(['get_points', 'get_badges']);
    },
  };
});
